import React, { createContext, useMemo, useState } from 'react'
import i18n from 'simple-react-i18n'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { Backdrop, Box, Stack } from '@mui/material'
import UserAction from '../user/actions/UserAction'
import useProgressDispatch from 'utils/customHook/useProgressDispatch'
import ProgressCard from 'components/card/ProgressCard'
import { compact, groupBy, isNil, keys, lowerCase, orderBy, toLower } from 'lodash'
import ContactAction from 'referencial/components/contact/actions/ContactAction'
import { INDEXED_MODULES, OBSERVATORY, OBSERVATORY_MODULE_ID, OBSERVATORY_STATION_TYPE_NAME, WIDGET, WIDGET_MODULE_ID } from './ObservatoryConstants'
import useTitle from 'utils/customHook/useTitle'
import AdministrationAction from 'administration/actions/AdministrationAction'
import CmsAction from 'events/actions/CmsAction'
import StationAction from 'station/actions/StationAction'
import QualityAction from 'quality/actions/QualityAction'
import ReferencialAction from 'referencial/action/ReferencialAction'
import useActions from 'utils/customHook/useActions'
import { getI18nOrLabel } from 'utils/StringUtil'
import FiltersPanel from 'administration/components/filters/FiltersPanel'
import ObservatoryContent from './ObservatoryContent'
import ModuleDialog from './ModuleDialog'
import CreateModuleDialog from './CreateModuleDialog'
import DuplicateModuleDialog from './DuplicateModuleDialog'

export const ModuleHabsContext = createContext()
export const ModuleHabsDispatchContext = createContext()

const AdminModulesApp = () => {
    const {
        applicativeUsers,
        contacts,
        userBookmarks,

        globalParameters,
        cmsEvents,
        cmsCategories,
        piezoDataTypes,
        qualityThresholds,
        sandreCodes,
        filters,
        users,
        usersModulesAccess,
        applicativeUsersParameters,
    } = useSelector(store => ({
        applicativeUsers: store.UserReducer.applicativeUsers,
        contacts: store.ContactReducer.contacts,
        userBookmarks: store.UserReducer.userBookmarks,

        globalParameters: store.AdministrationReducer.globalParameters,
        cmsEvents: store.EventsReducer.cmsEvents,
        cmsCategories: store.EventsReducer.cmsCategories,
        piezoDataTypes: store.StationReducer.piezoDataTypes,
        qualityThresholds: store.QualityReducer.thresholds,
        sandreCodes: store.ReferencialReducer.sandreCodes,
        filters: store.StationReducer.filters,
        users: store.UserReducer.users,
        usersModulesAccess: store.UserReducer.usersModulesAccess,
        applicativeUsersParameters: store.UserReducer.applicativeUsersParameters,
    }), shallowEqual)

    const [filter, setFilter] = useState({})

    const [moduleToOpen, setModuleToOpen] = useState({})
    const [openCreateModule, setOpenCreateModule] = useState(false)

    const [openDeleteModule, setOpenDeleteModule] = useState(false)
    const [modulesToDelete, setModulesToDelete] = useState([])

    const [openDuplicateModule, setOpenDuplicateModule] = useState(false)
    const [moduleToDuplicate, setModuleToDuplicate] = useState(undefined)

    const dispatch = useDispatch()

    const { isLoaded, progress } = useProgressDispatch(() => compact([
        !applicativeUsers.length && dispatch(UserAction.fetchApplicativeUsers()),
        !contacts.length && dispatch(ContactAction.fetchContacts()),
        !userBookmarks.length && dispatch(UserAction.fetchBookmarks()),
        !globalParameters.length && dispatch(AdministrationAction.fetchGlobalParameters()),
        !cmsEvents.length && dispatch(CmsAction.fetchCMSEvents()),
        !cmsCategories.length && dispatch(CmsAction.fetchCMSCategories()),
        !piezoDataTypes.length && dispatch(StationAction.fetchDataTypesByProject('SIES')),
        !qualityThresholds.length && dispatch(QualityAction.fetchThresholds()),
        !sandreCodes.length && dispatch(ReferencialAction.fetchSandreCodes()),
        !filters.length && dispatch(StationAction.fetchFilters()),
        !users.length && dispatch(UserAction.fetchUsers()),
        !usersModulesAccess.length && dispatch(UserAction.getUsersModulesAccess()),
        !applicativeUsersParameters.length && dispatch(UserAction.getApplicativeUsersParameters()),
    ]), [])

    useTitle(() => [{
        title: i18n.administration,
        href: 'administration',
    }, {
        title: i18n.modules,
        href: 'administration/modules',
    }], [])

    useActions(() => ({
        new: () => setOpenCreateModule(true),
        specificDelete: () => setOpenDeleteModule(true),
        duplicate: () => setOpenDuplicateModule(true),
    }), [])

    const onCloseDuplicateModule = () => {
        setOpenDuplicateModule(false)
        setModuleToDuplicate(undefined)
    }

    const onCloseDeleteModule = () => {
        setOpenDeleteModule(false)
        setModulesToDelete([])
    }

    const onDeleteModules = () => {
        dispatch(UserAction.deleteUsers(modulesToDelete.map(mtd => mtd.login), () => {
            dispatch(UserAction.fetchApplicativeUsers())
            dispatch(ContactAction.fetchContacts(true))
            onCloseDeleteModule()
        }))
    }

    const setDeleteModules = mod => setModulesToDelete(prev => !prev.some(p => p.login === mod.login) ? [...prev, mod] : prev.filter(p => p.login !== mod.login))

    const observatoryBookmarks = useMemo(() => userBookmarks.filter(b => b.stationType === OBSERVATORY_MODULE_ID).map(b => b.identifiant), [userBookmarks])
    const widgetBookmarks = useMemo(() => userBookmarks.filter(b => b.stationType === WIDGET_MODULE_ID).map(b => b.identifiant), [userBookmarks])

    const modulesKeys = keys(OBSERVATORY_STATION_TYPE_NAME)
    const modulesKeysWithLabel = modulesKeys.map(key => ({ key: toLower(key), label: getI18nOrLabel(OBSERVATORY_STATION_TYPE_NAME[key].labelKey) }))
    const groupedUsersModulesAccess = groupBy(usersModulesAccess, 'login')

    const observatories = useMemo(() => applicativeUsers
        .filter(({ isApplicative, login }) => isApplicative && lowerCase(login).includes(OBSERVATORY.conventionalName))
        .map(obs => {
            const userModules = groupedUsersModulesAccess[obs.login] ?? []
            const activeModules = userModules.reduce((acc, uma) => {
                const toLowerHab = toLower(uma.habilitation)
                const habKey = modulesKeysWithLabel.find(mk => mk.key.startsWith(toLowerHab))
                if (!isNil(habKey)) acc.push(habKey?.label)
                return acc
            }, [])

            const foundModule = INDEXED_MODULES[obs.login.split('_')[0]] || OBSERVATORY
            const obsName = contacts.find(c => c.code === obs.contactCode)?.name || obs.login

            return {
                ...obs,
                name: obsName,
                isFav: observatoryBookmarks.some(code => code === obs.login),
                moduleTypeId: foundModule.moduleTypeId,
                conventionalName: foundModule.conventionalName,
                module: foundModule.name,
                activeModules,
            }
        }), [applicativeUsers, observatoryBookmarks, usersModulesAccess, contacts])

    const widgets = useMemo(() => applicativeUsers
        .filter(({ isApplicative, login }) => isApplicative && (lowerCase(login).includes(WIDGET.conventionalName) || WIDGET.modules.some(m => lowerCase(login).includes(m.conventionalName))))
        .map(widget => {
            const userModules = groupedUsersModulesAccess[widget.login] ?? []
            const activeModules = userModules.reduce((acc, uma) => {
                const toLowerHab = toLower(uma.habilitation)
                const habKey = modulesKeysWithLabel.find(mk => mk.key.startsWith(toLowerHab))
                if (!isNil(habKey)) acc.push(habKey?.label)
                return acc
            }, [])

            const foundModule = INDEXED_MODULES[widget.login.split('_')[0]] || WIDGET
            const widgetName = contacts.find(c => c.code === widget.contactCode)?.name || widget.login

            return {
                ...widget,
                name: widgetName,
                isFav: widgetBookmarks.some(code => code === widget.login),
                moduleTypeId: foundModule.moduleTypeId,
                conventionalName: foundModule.conventionalName,
                module: foundModule.name,
                activeModules,
            }
        }), [applicativeUsers, widgetBookmarks, usersModulesAccess, contacts])

    const modules = orderBy([...observatories, ...widgets], ['isFav', 'name'], ['desc', 'asc'])

    return !isLoaded ? (
        <Box sx={{ padding: 1.5 }}><ProgressCard progress={progress} /></Box>
    ) : (
        <Stack spacing={1.5} sx={{ padding: 1.5, height: 'calc(100% - 64px)' }}>
            <FiltersPanel
                defaultFilter={filter}
                onValidate={setFilter}

                variant='newCard'
            />

            <Backdrop
                sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })}
                open={openDeleteModule || openDuplicateModule}
            />

            <ObservatoryContent
                filter={filter}
                modules={modules}
                modulesToDelete={modulesToDelete}
                setModuleToOpen={mod => {
                    if (openDeleteModule) {
                        setDeleteModules(mod)
                    } else if (openDuplicateModule) {
                        setModuleToDuplicate(mod)
                    } else {
                        setModuleToOpen(mod)
                    }
                }}
                onDeleteModules={openDeleteModule && onDeleteModules}
                onCloseDelete={openDeleteModule ? onCloseDeleteModule : onCloseDuplicateModule}
                backdropped={openDeleteModule || openDuplicateModule}
            />

            <ModuleDialog
                open={keys(moduleToOpen).length}
                onClose={() => setModuleToOpen({})}

                mod={moduleToOpen}
            />
            <CreateModuleDialog
                open={openCreateModule}
                onClose={() => setOpenCreateModule(false)}
            />
            <DuplicateModuleDialog
                moduleToDuplicate={moduleToDuplicate}
                onClose={onCloseDuplicateModule}
            />
        </Stack>
    )
}

export default AdminModulesApp
